// Story 1.18 — Refund modal. Lets the user enter an amount up to the
// refundable balance for a specific top-up + confirms.
//
//   * Amount input validates > 0 and ≤ refundable.
//   * Per-submit crypto.randomUUID() idempotency key.
//   * 422 REFUND_INELIGIBLE inline error — if amount_exceeds_refundable,
//     prefill the input with the (now-known) refundable amount.
//   * Any other failure: inline error banner; user can retry.

import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@greenhold/components";
import toast from "react-hot-toast";

import { useRefundRequest, type RefundError } from "./useRefundRequest";
import type { RefundableTopupItem } from "./useRefundEligibleList";

function formatEur(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const euros = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${sign}€${euros}.${remainder.toString().padStart(2, "0")}`;
}

function parseEurToCents(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const num = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(num) || num <= 0) return null;
  return Math.round(num * 100);
}

type RefundModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: RefundableTopupItem | null;
};

export function RefundModal({ open, onOpenChange, item }: RefundModalProps) {
  const [amountInput, setAmountInput] = useState<string>("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const refund = useRefundRequest();
  // Depend on the stable `reset` fn, not the whole mutation object — see
  // CashoutModal for the infinite-loop rationale (new object identity every
  // render + reset() notifying the observer = "Maximum update depth").
  const { reset: resetRefund } = refund;

  useEffect(() => {
    if (!open) {
      setAmountInput("");
      setInlineError(null);
      resetRefund();
    }
  }, [open, resetRefund]);

  // When a new item is selected, default the input to the full
  // refundable amount as a sensible starting value.
  useEffect(() => {
    if (open && item) {
      const eur = item.refundable_amount_cents / 100;
      setAmountInput(eur.toFixed(2));
    }
  }, [open, item]);

  if (!item) return null;

  const amountCents = parseEurToCents(amountInput);
  const validationError = ((): string | null => {
    if (amountCents == null) return null;
    if (amountCents > item.refundable_amount_cents)
      return `Refundable: ${formatEur(item.refundable_amount_cents)}.`;
    return null;
  })();

  const canSubmit =
    amountCents != null && validationError == null && !refund.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || amountCents == null) return;
    setInlineError(null);
    try {
      await refund.mutateAsync({
        body: {
          ledger_entry_id: item.ledger_entry_id,
          amount_cents: amountCents,
        },
        idempotencyKey: crypto.randomUUID(),
      });
      toast.success(
        `Refund submitted — €${(amountCents / 100).toFixed(2)} on the way to ${item.card_hint}.`,
      );
      onOpenChange(false);
    } catch (err) {
      const detail = err as RefundError;
      // If the server says the amount exceeds refundable, prefill the
      // input with the now-known refundable amount so the user can
      // retry with one click.
      if (
        detail.reason === "amount_exceeds_refundable" &&
        typeof detail.refundable_amount_cents === "number"
      ) {
        setAmountInput((detail.refundable_amount_cents / 100).toFixed(2));
        setInlineError(detail.message);
        return;
      }
      setInlineError(detail.message ?? "Refund failed. Try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Refund to source card</DialogTitle>
          <DialogDescription>
            Refund unspent balance back to the card that funded the
            original top-up.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm">
            <span className="text-muted-foreground">From your </span>
            <span className="font-semibold tabular-nums">
              {formatEur(item.original_amount_cents)}
            </span>
            <span className="text-muted-foreground"> top-up to </span>
            <span className="font-mono">{item.card_hint}</span>
          </div>

          <div className="text-sm">
            <span className="text-muted-foreground">Available to refund: </span>
            <span className="font-semibold tabular-nums">
              {formatEur(item.refundable_amount_cents)}
            </span>
          </div>

          <div className="space-y-1">
            <Label htmlFor="refund-amount">Amount (EUR)</Label>
            <Input
              id="refund-amount"
              type="text"
              inputMode="decimal"
              autoFocus
              value={amountInput}
              onChange={(e) => {
                setAmountInput(e.target.value);
                setInlineError(null);
              }}
              placeholder="0.00"
            />
            <p className="text-xs text-muted-foreground">
              {validationError ?? "Arrives back on your card in 5–10 business days."}
            </p>
          </div>

          {inlineError && (
            <p className="text-sm text-destructive" role="alert">
              {inlineError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="tertiary"
              onClick={() => onOpenChange(false)}
              disabled={refund.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {refund.isPending ? "Submitting…" : "Confirm refund"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
